import { parseISO, areIntervalsOverlapping, isSameDay } from 'date-fns';
import { DateTime } from './interfaces';
import { formatTimeHHMM } from '../../utilities';
import { TimePickerValue } from '../../components/time-picker';
import { AttendanceSheetStateDto, LessonStateDto } from '../../api-service/clients';

export type TimeObject = { hours: number; minutes: number };

export const REFRESH_COURSE_LESSON_TABLE = 'REFRESH_COURSE_LESSON_TABLE';
export const REFRESH_CITIZEN_SCHEDULE = 'REFRESH_CITIZEN_SCHEDULE';

export function toSchultzDate(value: string | Date): Date {
  if (typeof value === 'string') {
    return parseISO(value);
  }
  return value;
}

export function toTime(value: string | Date | TimeObject | undefined): TimePickerValue {
  if (isTimeObject(value)) {
    return value;
  }
  if (value) {
    return {
      hours: toSchultzDate(value).getHours(),
      minutes: toSchultzDate(value).getMinutes(),
    };
  }
  return undefined;
}

export function formatTime(value?: string | Date | TimeObject): string | undefined {
  if (value) {
    if (typeof value === 'string') {
      const date = parseISO(value);
      return formatTimeHHMM({ hours: date.getHours(), minutes: date.getMinutes() });
    }
    if (isTimeObject(value)) {
      return `${value.hours}:${value.minutes}`;
    }
    return formatTimeHHMM({ hours: value.getHours(), minutes: value.getMinutes() });
  }
  return undefined;
}

export function getTimeObjectFromTimeAndMinutes(timeObject: TimeObject, minutesToChange: number, subtract = false): TimeObject {
  const totalMinutes = timeObject.hours * 60 + timeObject.minutes + (subtract ? -minutesToChange : minutesToChange);

  const wrapped = ((totalMinutes % 1440) + 1440) % 1440; // wrap within 24h
  return {
    hours: Math.floor(wrapped / 60),
    minutes: wrapped % 60,
  };
}

export function getDateFromTimeAndMinutes(timeObject: TimeObject, minutesToChange: number, subtract = false): Date {
  const totalMinutes = timeObject.hours * 60 + timeObject.minutes + (subtract ? -minutesToChange : minutesToChange);

  const wrapped = ((totalMinutes % 1440) + 1440) % 1440;

  const date = new Date();
  date.setHours(Math.floor(wrapped / 60), wrapped % 60, 0, 0);
  return date;
}

export function toMinutes(timeObject: TimeObject): number {
  return timeObject.hours * 60 + timeObject.minutes;
}

export function getOverlappingDates(occupiedDates: DateTime[], lessonDates: DateTime[]): DateTime[] {
  const overlaps = occupiedDates.filter(occupiedDate =>
    lessonDates.some(lessonDate => areIntervalsOverlapping({ start: occupiedDate.start, end: occupiedDate.end }, { start: lessonDate.start, end: lessonDate.end }))
  );
  return overlaps;
}

export function isClosedDay(date: DateTime): boolean {
  return date.dateType === 'holiday' || date.dateType === 'centerClosedDay';
}

export function excludeClosedDays(lessonDates: Date[], closedDays: DateTime[]): Date[] {
  return lessonDates.filter(lessonDate => !closedDays.some(closedDay => isSameDay(closedDay.start, lessonDate)));
}

function isTimeObject(value: unknown): value is TimeObject {
  return typeof value === 'object' && value !== null && 'hours' in value && 'minutes' in value && typeof value.hours === 'number' && typeof value.minutes === 'number';
}

export function isAttendanceSheetLocked(attendanceSheetState?: AttendanceSheetStateDto): boolean {
  return attendanceSheetState === AttendanceSheetStateDto.ReadyForApproval || attendanceSheetState === AttendanceSheetStateDto.Submitted;
}

export function getLessonStateDisplayValue(lessonState: LessonStateDto) {
  switch (lessonState) {
    case LessonStateDto.Draft:
      return 'Kladde';
    case LessonStateDto.Published:
      return 'Publiceret';
    case LessonStateDto.Cancelled:
      return 'Aflyst';
    default:
      return 'Ukendt';
  }
}

export function getHoursAndMinutes(timeInMinutes: number) {
  const hours = Math.floor(timeInMinutes / 60);
  const minutes = timeInMinutes % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'time' : 'timer'}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? 'minut' : 'minutter'}`);
  }

  if (hours === 0 && minutes === 0) {
    parts.push('0 minutter');
  }

  return parts.join(' og ');
}
